import { useContext } from 'react';

import { useBreakpointValue } from '@vimeo/bokeh';
import {
  SubjectKeyTypes,
  useEppoAssignment,
  VariationTypes,
  type ContextOverrides,
} from '@vimeo/eppo';
import { translate } from '@vimeo/i18n-runtime';
import { getPageNameFromPath } from '@vimeo/ui/core/F2PJoinLogin/utils';
import getDeviceType from '@vimeo/ui/core/utils/getDeviceType';
import type { WebBpContext } from '@vimeo/use-tracking';
import { ViewerContext } from '@vimeo/viewer';

export const JOIN_CTA_FEATURE_FLAG = 'join-vimeo-ctas';
export const JOIN_CTA_FEATURE_FLAG_VARIANTS = {
  CONTROL: 'control',
  VARIANT_GET: 'variant-get',
  VARIANT_JOIN: 'variant-join',
};
export const JOIN_CTA_ASSIGNMENT_MAP = {
  [JOIN_CTA_FEATURE_FLAG_VARIANTS.CONTROL]: {
    desktopCopy: 'Join',
    desktopTranslation: translate('Join'),
    mobileCopy: 'Join',
    mobileTranslation: translate('Join'),
  },
  [JOIN_CTA_FEATURE_FLAG_VARIANTS.VARIANT_GET]: {
    desktopCopy: 'Get Vimeo for Free',
    desktopTranslation: translate('Get Vimeo for Free'),
    mobileCopy: 'Get for Free',
    mobileTranslation: translate('Get for Free'),
  },
  [JOIN_CTA_FEATURE_FLAG_VARIANTS.VARIANT_JOIN]: {
    desktopCopy: 'Join Vimeo for Free',
    desktopTranslation: translate('Join Vimeo for Free'),
    mobileCopy: 'Join for Free',
    mobileTranslation: translate('Join for Free'),
  },
};

export const useJoinCtaAssignment = () => {
  const viewer = useContext(ViewerContext);
  const vuid = viewer?.vuid || 0;

  const referrer: WebBpContext['referrer'] = document.referrer;
  const referrerPageName: WebBpContext['referrer_page_name'] = getPageNameFromPath(
    `/${document.referrer}`,
  );
  let pathName: WebBpContext['path'] = '/top-nav';
  let pageName: WebBpContext['page_name'] = 'other';

  if (typeof window !== 'undefined') {
    pathName = window.location?.pathname;
    pageName = getPageNameFromPath(pathName);
  }

  const subject = { key: vuid, keyType: SubjectKeyTypes.VUID };
  const bigPictureOverrides: ContextOverrides = {
    product_analytics_context: {
      device_type: getDeviceType(),
      feature: 'join',
      location: 'top_navigation_bar',
      product: 'community',
    },
    web_context: {
      page_name: pageName,
      path: pathName,
      referrer,
      referrer_page_name: referrerPageName,
    },
  };

  const { assignment } = useEppoAssignment({
    flagKey: JOIN_CTA_FEATURE_FLAG,
    subject,
    bigPictureOverrides,
    defaultAssignment: JOIN_CTA_FEATURE_FLAG_VARIANTS.CONTROL,
    assignmentType: VariationTypes.STRING,
    disabled: !vuid,
  }) as { assignment: string };

  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: 'md' });
  let ctaText = 'Join';
  let ctaTextTranslated = translate('Join');

  if (isMobile && JOIN_CTA_ASSIGNMENT_MAP[assignment]) {
    ctaText = JOIN_CTA_ASSIGNMENT_MAP[assignment].mobileCopy;
    ctaTextTranslated = JOIN_CTA_ASSIGNMENT_MAP[assignment].mobileTranslation;
  } else if (JOIN_CTA_ASSIGNMENT_MAP[assignment]) {
    ctaText = JOIN_CTA_ASSIGNMENT_MAP[assignment].desktopCopy;
    ctaTextTranslated = JOIN_CTA_ASSIGNMENT_MAP[assignment].desktopTranslation;
  }

  return { assignment, ctaText, ctaTextTranslated };
};
